import { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } from '@zxing/library';

const defaultFormats = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.EAN_8,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
  BarcodeFormat.RSS_14,
];
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

type UseScannerProps = {
  videoElementId: string;
  handleError: (error: Error) => void;
  readInterval?: number;
  formats?: BarcodeFormat[];
  delayNextScan?: boolean;
};

export const useScanner = ({
  videoElementId,
  readInterval = 500,
  handleError,
  formats = defaultFormats,
  delayNextScan,
}: UseScannerProps): string => {
  const reader = useRef<BrowserMultiFormatReader>();
  const [data, setData] = useState('');

  useEffect(() => {
    async function initReader() {
      try {
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
        reader.current = new BrowserMultiFormatReader(hints, readInterval);
        const devices = await reader.current.listVideoInputDevices();
        const { deviceId = undefined } = devices.length > 1 ? devices[1] : devices.length > 0 ? devices[0] : {};
        delayNextScan && (await delay(500));
        const data = await reader.current.decodeOnceFromVideoDevice(deviceId, videoElementId);
        setData(data.getText());
      } catch (err) {
        handleError(err);
      }
    }
    initReader();
    return () => {
      reader.current?.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoElementId, readInterval, formats]);
  return data;
};
